<template>
  <div class="select-text">
    <div
      id="prospect-details-panel"
      class="prospect-details-panel fixed w-screen top-18 flex justify-end transition-all duration-500"
      :style="{
        right: open ? '0vw' : '-200vw',
        height: 'calc(100vh - 4.5rem)',
        zIndex: 51
      }"
    >
      <div
        class="h-full w-screen absolute left-0 top-0 -z-10"
        @click="$emit('close-panel')"
      ></div>

      <div
        class="h-full w-5 rounded-l-3xl flex-shrink-0 relative z-0"
        :style="{
          background: 'linear-gradient(180deg, #FFE4E8 0%, #9A8AC2 100%)'
        }"
      ></div>

      <div
        class="h-full w-120 flex-shrink-0 flex flex-col bg-pure-white relative z-0"
      >
        <div
          v-if="!panelLoading && prospectStage"
          class="pt-4 pb-3 px-6 flex flex-col gap-1"
        >
          <div class="relative flex items-center justify-between">
            <div class="flex items-center gap-2">
              <div
                class="select-move-prospect-stage rounded-full flex-shrink-0 min-h-6 px-3 flex items-center gap-1 transition-all duration-300 cursor-pointer"
                :class="[
                  prospectStage.is_discontinued
                    ? 'bg-rose-200'
                    : prospectStage.is_admitted
                    ? 'bg-lime-200 pointer-events-none'
                    : 'bg-cornflower-200'
                ]"
                @click="stageOptionsOpened = !stageOptionsOpened"
              >
                <BlTextBody3
                  text="Current stage:"
                  class="current-stage-text"
                  :class="[
                    stageOptionsOpened ? 'flex' : '',
                    prospectStage.is_discontinued
                      ? 'text-rose-700'
                      : prospectStage.is_admitted
                      ? 'text-lime-700'
                      : 'text-cornflower-800'
                  ]"
                />
                <BlIcon
                  v-if="prospectStage.is_admitted"
                  icon="confetti"
                  color="lime-7"
                  class="w-4 h-4"
                />
                <BlTextSubtitle3
                  :text="prospectStage.stage.name"
                  :class="[
                    prospectStage.is_discontinued
                      ? 'text-rose-700'
                      : prospectStage.is_admitted
                      ? 'text-lime-700'
                      : 'text-cornflower-800'
                  ]"
                />
                <BlIcon
                  v-if="prospectStage.is_admitted"
                  icon="confetti"
                  color="lime-7"
                  class="w-4 h-4"
                />
                <BlIcon
                  v-if="open && !prospectStage.is_admitted"
                  icon="chevron-down"
                  :color="
                    prospectStage.is_discontinued ? 'error' : 'cornflower-8'
                  "
                  class="w-4 h-4 transition-all duration-300"
                  :style="{ rotate: stageOptionsOpened ? '180deg' : '' }"
                />
              </div>
              <div v-if="moveProspectLoading" class="blum-loader-xs"></div>
            </div>
            <div
              v-if="stageOptionsOpened"
              class="absolute z-50 top-8 left-0 p-1 gap-1 flex flex-col min-w-40 bg-pure-white rounded border border-slate-400 truncate"
            >
              <div class="px-4 py-2 flex text-left rounded max-w-80">
                <BlTextBody2 text="Move to:" class="text-slate-1000" />
              </div>
              <div
                v-for="stage in stages"
                :key="stage.id"
                :title="stage.name"
                class="px-2 py-1.5 flex rounded max-w-80"
                :class="[
                  stage.id === prospectStage.stage.id
                    ? 'bg-slate-100 pointer-events-none'
                    : 'hover:bg-hover-state cursor-pointer'
                ]"
                @click="openMoveProspect(stage.id)"
              >
                <BlChip :chip="stage.name" />
              </div>
            </div>
            <BlIcon
              v-if="open"
              icon="close"
              color="slate-6"
              class="h-6 w-6"
              clickable
              @clicked="$emit('close-panel')"
            />
          </div>
          <BlTextTitle4
            :text="prospectStage.prospect.name"
            :title="prospectStage.prospect.name"
            class="truncate"
          />
          <span v-if="prospectStage.is_discontinued">
            <BlTextBody2
              v-if="prospectStage.discontinuation_reason === 'other'"
              :text="`Reason: ${prospectStage.other_discontinuation_reason}`"
            />
            <BlTextBody2
              v-else
              :text="`Reason: ${prospectStage.discontinuation_reason_text}`"
            />
          </span>
        </div>
        <div
          v-if="panelLoading"
          class="h-full grid items-center justify-center"
        >
          <div class="blum-loader"></div>
        </div>
        <div
          v-else
          class="relative h-full flex flex-col justify-between overflow-y-auto"
        >
          <div class="flex flex-col">
            <div
              class="h-10 w-full px-2 flex gap-2 overflow-x-auto overflow-y-hidden scrollbar-lg"
              title="'Shift key + scroll mouse' for horizontal scrolling"
            >
              <div
                v-for="tab in tabs"
                :key="tab.value"
                class="relative pb-2 flex-shrink-0 cursor-pointer border-b border-slate-400"
                @click="activeTab = tab.value"
              >
                <div class="px-4">
                  <BlTextSubtitle2
                    v-if="tab.value === activeTab"
                    :text="tab.label"
                  />
                  <BlTextBody1 v-else :text="tab.label" />
                </div>
                <div
                  class="absolute bottom-0 h-1 w-full rounded transition-all duration-300"
                  :class="[
                    tab.value === activeTab
                      ? 'bg-light-purple'
                      : 'bg-pure-white'
                  ]"
                ></div>
              </div>
            </div>
            <StageInstructionTab
              v-if="activeTab === 'stage-instruction' && prospectStage"
            />
            <InstructionHistoryTab
              v-if="activeTab === 'instruction-history' && prospectStage"
            />
            <ClientInformationTab
              v-if="activeTab === 'client-information' && prospectStage"
              @after-commit="$emit('after-commit')"
            />
            <ActivityHistoryTab
              v-if="activeTab === 'activity-history' && prospectStage"
            />
          </div>
        </div>

        <NewInstructionProspectPanel
          v-if="
            !panelLoading && open && prospectStage && id === prospectStage.id
          "
        />
      </div>
    </div>

    <MoveProspectModal
      v-if="
        !panelLoading &&
          open &&
          prospectStage &&
          id === prospectStage.id &&
          moveProspectOpened
      "
      :location="location"
      :prospectStage="prospectStage"
      :destinationStage="destinationStage"
      @close-modal="moveProspectOpened = false"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";

import StageInstructionTab from "./prospectPanel/StageInstructionTab.vue";
import InstructionHistoryTab from "./prospectPanel/InstructionHistoryTab.vue";
import ClientInformationTab from "./prospectPanel/ClientInformationTab.vue";
import ActivityHistoryTab from "./prospectPanel/ActivityHistoryTab.vue";
import NewInstructionProspectPanel from "./prospectPanel/NewInstructionProspectPanel.vue";
import MoveProspectModal from "./MoveProspectModal.vue";

import BlIcon from "../../BlIcon.vue";
import BlChip from "../../BlChip.vue";
import BlTextTitle4 from "../../BlTextTitle4.vue";
import BlTextSubtitle2 from "../../BlTextSubtitle2.vue";
import BlTextSubtitle3 from "../../BlTextSubtitle3.vue";
import BlTextBody1 from "../../BlTextBody1.vue";
import BlTextBody2 from "../../BlTextBody2.vue";
import BlTextBody3 from "../../BlTextBody3.vue";
import BlInputText from "../../BlInputText.vue";
import BlFileHolder from "../../BlFileHolder.vue";
import BlInputButton from "../../BlInputButton.vue";
import BlBackdrop from "../../BlBackdrop.vue";

export default {
  name: "ProspectPanel",
  components: {
    StageInstructionTab,
    InstructionHistoryTab,
    ClientInformationTab,
    ActivityHistoryTab,
    NewInstructionProspectPanel,
    MoveProspectModal,
    BlIcon,
    BlChip,
    BlTextTitle4,
    BlTextSubtitle2,
    BlTextSubtitle3,
    BlTextBody1,
    BlTextBody2,
    BlTextBody3,
    BlInputText,
    BlFileHolder,
    BlInputButton,
    BlBackdrop
  },
  props: {
    location: { type: String, default: "table" },
    open: { type: Boolean, default: false },
    id: { type: Number, required: true }
    // @after-commit
  },
  data() {
    return {
      panelLoading: false,
      stageOptionsOpened: false,
      moveProspectOpened: false,
      moveProspectLoading: false,
      destinationStageId: 0,
      //
      activeTab: "", // stage-instruction | instruction-history | client-information | activity-history,
      tabs: [
        { value: "stage-instruction", label: "Stage Instruction" },
        { value: "instruction-history", label: "Instruction History" },
        { value: "client-information", label: "Client Information" },
        { value: "activity-history", label: "Activity History" }
      ]
    };
  },
  computed: {
    ...mapGetters(["stages", "prospectStage"]),
    destinationStage() {
      if (!this.destinationStageId) return {};
      return this.stages.find(i => i.id === this.destinationStageId) || {};
    }
  },
  watch: {
    open(val) {
      this.activeTab = "";
      if (val) {
        this.panelLoading = true;
        this.fetchProspectStage();
      } else {
        this.getProspectStage({ id: null });
      }
    },
    stageOptionsOpened(val) {
      if (val) {
        setTimeout(() => {
          window.addEventListener("click", this.closeHandlerStageOpened);
        }, 10);
      }
    }
  },
  methods: {
    ...mapActions([
      "getProspectStages",
      "getProspectStage",
      "getStageProspectStages",
      "moveProspect"
    ]),
    inputHandler(payload) {
      payload.input.model = payload.value;
    },
    fetchProspectStage() {
      const data = { id: this.id };
      this.getProspectStage(data).then(({ success }) => {
        this.panelLoading = false;
        if (!success) return;
        setTimeout(() => {
          this.activeTab = "stage-instruction";
        }, 100);
      });
    },
    closeHandlerStageOpened() {
      window.removeEventListener("click", this.closeHandlerStageOpened);
      this.stageOptionsOpened = false;
    },
    openMoveProspect(stageId) {
      this.destinationStageId = stageId;
      if (
        this.destinationStage.is_admitted ||
        this.destinationStage.is_discontinued
      ) {
        this.moveProspectOpened = true;
      } else {
        const data = {
          id: this.prospectStage.prospect.id,
          prospect_name: this.prospectStage.prospect.name,
          stage_id: stageId,
          stage_name: this.destinationStage.name
        };
        this.moveProspectLoading = true;
        this.moveProspect(data).then(async ({ success }) => {
          if (!success) {
            this.moveProspectLoading = false;
            return;
          }
          if (this.location === "table") {
            // await this.getProspectStages();
            this.$emit("after-commit");
          }
          if (this.location === "kanban") {
            await this.getStageProspectStages({
              id: this.prospectStage.stage.id
            });
            await this.getStageProspectStages({ id: stageId });
          }
          this.moveProspectLoading = false;
          this.$emit("close-panel");
        });
      }
    }
  }
};
</script>

<style lang="scss">
.prospect-details-panel {
  .select-move-prospect-stage {
    .current-stage-text {
      display: none;
    }
  }
  .select-move-prospect-stage:hover {
    .current-stage-text {
      display: flex;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/salesPipeline/modal/ProspectPanel.vue